import React from "react";
import { Link } from "react-router-dom";
import { Pencil } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { cn } from "../../../lib/utils";
import { RepositoryViewModel } from "../types";
import RepositoryPrimaryInfo from "./RepositoryPrimaryInfo";
import RepositoryFavoriteButton from "./RepositoryFavoriteButton";
import RepositoryActionsDropdown from "./RepositoryActionsDropdown";
import RepositoryContextMenu from "./RepositoryContextMenu";

interface RepositoryListRowProps {
  repository: RepositoryViewModel;
  onToggleFavorite: (repo: RepositoryViewModel) => void;
  onOpenRepository: (repo: RepositoryViewModel) => void;
}

const RepositoryListRow = ({
  repository,
  onToggleFavorite,
  onOpenRepository,
}: RepositoryListRowProps) => {
  return (
    <RepositoryContextMenu
      owner={repository.owner}
      name={repository.name}
      repoUrl={repository.repoUrl}
      isFavorite={repository.isFavorite}
      onToggleFavorite={() => onToggleFavorite(repository)}
    >
      <div
        tabIndex={0}
        role="button"
        className={cn(
          "grid grid-cols-1 items-center gap-3 rounded-md border p-3 transition-colors hover:bg-accent/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          "md:grid-cols-[minmax(0,1fr)_auto] hover:bg-gray-50 hover:shadow-sm"
        )}
        onClick={() => onOpenRepository(repository)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onOpenRepository(repository);
          }
        }}
      >
        <RepositoryPrimaryInfo repository={repository} />
        <div className="flex items-center justify-end gap-1">
          <Button asChild size="sm" variant="outline">
            <Link
              to={`/edit/${repository.owner}/${repository.name}`}
              onClick={(event) => event.stopPropagation()}
            >
              <Pencil className="mr-1 h-4 w-4" /> Open editor
            </Link>
          </Button>
          <RepositoryFavoriteButton
            isFavorite={repository.isFavorite}
            onToggle={() => onToggleFavorite(repository)}
          />
          <RepositoryActionsDropdown
            repository={repository}
            onToggleFavorite={() => onToggleFavorite(repository)}
          />
        </div>
      </div>
    </RepositoryContextMenu>
  );
};

export default RepositoryListRow;
